import React from 'react';
import { Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { Input } from '../../ui/Input';
import type { FieldType, FieldOption } from '../../../types/forms';

/** Field types que têm lista de opções editáveis. */
export const OPTION_TYPES: FieldType[] = ['select', 'checkbox', 'radio', 'multiselect'];

type RawOption = string | FieldOption;

export interface FieldOptionsEditorProps {
  fieldType: FieldType;
  options: RawOption[];
  onChange: (options: RawOption[]) => void;
}

/**
 * Controlled editor de opções: reorder (up/down), inline edit, delete, add.
 * Retorna null para field types que não têm options.
 *
 * Preserva shape do FormField.options (string | FieldOption — backward compat
 * com scoring em single-choice). Só mexe no `.label` ou no próprio string.
 */
export const FieldOptionsEditor: React.FC<FieldOptionsEditorProps> = ({
  fieldType,
  options,
  onChange,
}) => {
  if (!OPTION_TYPES.includes(fieldType)) return null;

  const len = options.length;

  const updateOptionLabel = (index: number, newLabel: string) => {
    const next = [...options];
    const current = next[index];
    next[index] = typeof current === 'string' ? newLabel : { ...current, label: newLabel };
    onChange(next);
  };

  const moveUp = (index: number) => {
    if (index === 0) return;
    const next = [...options];
    [next[index - 1], next[index]] = [next[index], next[index - 1]];
    onChange(next);
  };

  const moveDown = (index: number) => {
    if (index === len - 1) return;
    const next = [...options];
    [next[index], next[index + 1]] = [next[index + 1], next[index]];
    onChange(next);
  };

  const remove = (index: number) => {
    onChange(options.filter((_, i) => i !== index));
  };

  const addOption = () => {
    onChange([...options, `Opção ${len + 1}`]);
  };

  return (
    <div>
      <label className="block text-sm font-medium text-neutral-200 mb-1.5">Opções</label>
      <div className="space-y-1.5">
        {options.map((rawOption, index) => {
          const optionValue = typeof rawOption === 'string' ? rawOption : rawOption.label;
          return (
            <div key={index} className="flex gap-1 items-center">
              <div className="flex flex-col">
                <button
                  onClick={() => moveUp(index)}
                  disabled={index === 0}
                  className="p-0.5 text-neutral-500 hover:text-white disabled:opacity-30 disabled:hover:text-neutral-500"
                  title="Mover para cima"
                >
                  <ChevronUp className="w-3 h-3" />
                </button>
                <button
                  onClick={() => moveDown(index)}
                  disabled={index === len - 1}
                  className="p-0.5 text-neutral-500 hover:text-white disabled:opacity-30 disabled:hover:text-neutral-500"
                  title="Mover para baixo"
                >
                  <ChevronDown className="w-3 h-3" />
                </button>
              </div>
              <Input
                value={optionValue}
                onChange={(e) => updateOptionLabel(index, e.target.value)}
              />
              <button
                onClick={() => remove(index)}
                className="p-2 text-neutral-400 hover:text-red-400"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          );
        })}
        <button
          onClick={addOption}
          className="w-full p-2 border border-dashed border-neutral-700 rounded-xl text-sm text-neutral-400 hover:text-white hover:border-neutral-600 transition-colors"
        >
          + Adicionar opção
        </button>
      </div>
    </div>
  );
};
